import { Directive, HostListener, Renderer, ElementRef, Output, Input, EventEmitter, ContentChildren, NgZone } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { merge } from 'rxjs/observable/merge';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/takeUntil';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/pairwise';
import 'rxjs/add/operator/take';
import { ResizeHandle } from './resizeHandle.directive';
function isNumberCloseTo(value1, value2, precision) {
    if (precision === void 0) { precision = 3; }
    var diff = Math.abs(value1 - value2);
    return diff < precision;
}
function getNewBoundingRectangle(startingRect, edges, mouseX, mouseY) {
    var newBoundingRect = {
        top: startingRect.top,
        bottom: startingRect.bottom,
        left: startingRect.left,
        right: startingRect.right
    };
    if (edges.top) {
        newBoundingRect.top += mouseY;
    }
    if (edges.bottom) {
        newBoundingRect.bottom += mouseY;
    }
    if (edges.left) {
        newBoundingRect.left += mouseX;
    }
    if (edges.right) {
        newBoundingRect.right += mouseX;
    }
    newBoundingRect.height = newBoundingRect.bottom - newBoundingRect.top;
    newBoundingRect.width = newBoundingRect.right - newBoundingRect.left;
    return newBoundingRect;
}
function isWithinBoundingY(_a) {
    var mouseY = _a.mouseY, rect = _a.rect;
    return mouseY >= rect.top && mouseY <= rect.bottom;
}
function isWithinBoundingX(_a) {
    var mouseX = _a.mouseX, rect = _a.rect;
    return mouseX >= rect.left && mouseX <= rect.right;
}
function getResizeEdges(_a) {
    var mouseX = _a.mouseX, mouseY = _a.mouseY, elm = _a.elm, allowedEdges = _a.allowedEdges, cursorPrecision = _a.cursorPrecision;
    var elmPosition = elm.nativeElement.getBoundingClientRect();
    var edges = {};
    if (allowedEdges.left &&
        isNumberCloseTo(mouseX, elmPosition.left, cursorPrecision) &&
        isWithinBoundingY({ mouseY: mouseY, rect: elmPosition })) {
        edges.left = true;
    }
    if (allowedEdges.right &&
        isNumberCloseTo(mouseX, elmPosition.right, cursorPrecision) &&
        isWithinBoundingY({ mouseY: mouseY, rect: elmPosition })) {
        edges.right = true;
    }
    if (allowedEdges.top &&
        isNumberCloseTo(mouseY, elmPosition.top, cursorPrecision) &&
        isWithinBoundingX({ mouseX: mouseX, rect: elmPosition })) {
        edges.top = true;
    }
    if (allowedEdges.bottom &&
        isNumberCloseTo(mouseY, elmPosition.bottom, cursorPrecision) &&
        isWithinBoundingX({ mouseX: mouseX, rect: elmPosition })) {
        edges.bottom = true;
    }
    return edges;
}
var DEFAULT_RESIZE_CURSORS = Object.freeze({
    topLeft: 'nw-resize',
    topRight: 'ne-resize',
    bottomLeft: 'sw-resize',
    bottomRight: 'se-resize',
    leftOrRight: 'ew-resize',
    topOrBottom: 'ns-resize'
});
function getResizeCursor(edges, cursors) {
    if (edges.left && edges.top) {
        return cursors.topLeft;
    }
    else if (edges.right && edges.top) {
        return cursors.topRight;
    }
    else if (edges.left && edges.bottom) {
        return cursors.bottomLeft;
    }
    else if (edges.right && edges.bottom) {
        return cursors.bottomRight;
    }
    else if (edges.left || edges.right) {
        return cursors.leftOrRight;
    }
    else if (edges.top || edges.bottom) {
        return cursors.topOrBottom;
    }
    else {
        return null;
    }
}
function getEdgesDiff(_a) {
    var edges = _a.edges, initialRectangle = _a.initialRectangle, newRectangle = _a.newRectangle;
    var edgesDiff = {};
    Object.keys(edges).forEach(function (edge) {
        edgesDiff[edge] = newRectangle[edge] - initialRectangle[edge];
    });
    return edgesDiff;
}
var RESIZE_ACTIVE_CLASS = 'resize-active';
var RESIZE_LEFT_HOVER_CLASS = 'resize-left-hover';
var RESIZE_RIGHT_HOVER_CLASS = 'resize-right-hover';
var RESIZE_TOP_HOVER_CLASS = 'resize-top-hover';
var RESIZE_BOTTOM_HOVER_CLASS = 'resize-bottom-hover';
/**
 * Place this on an element to make it resizable
 *
 * For example
 *
 * ```
 * &lt;div mwlResizable [resizeEdges]="{bottom: true, right: true, top: true, left: true}" [enableGhostResize]="true"&gt;&lt;/div&gt;
 * ```
 */
export var Resizable = (function () {
    /**
     * @private
     */
    function Resizable(renderer, elm, zone) {
        this.renderer = renderer;
        this.elm = elm;
        this.zone = zone;
        /**
         * The edges that an element can be resized from. Pass an object like `{top: true, bottom: false}`. By default no edges can be resized.
         */
        this.resizeEdges = {};
        /**
         * Set to `true` to enable a temporary resizing effect of the element in between the `resizeStart` and `resizeEnd` events.
         */
        this.enableGhostResize = false;
        /**
         * A snap grid that resize events will be locked to.
         *
         * e.g. to only allow the element to be resized every 10px set it to `{left: 10, right: 10}`
         */
        this.resizeSnapGrid = {};
        /**
         * The mouse cursors that will be set on the resize edges
         */
        this.resizeCursors = DEFAULT_RESIZE_CURSORS;
        /**
         * Mouse over thickness to active cursor.
         */
        this.resizeCursorPrecision = 3;
        /**
         * Called when the mouse is pressed and a resize event is about to begin. `$event` is a `ResizeEvent` object.
         */
        this.resizeStart = new EventEmitter(false);
        /**
         * Called as the mouse is dragged after a resize event has begun. `$event` is a `ResizeEvent` object.
         */
        this.resizing = new EventEmitter(false);
        /**
         * Called after the mouse is released after a resize event. `$event` is a `ResizeEvent` object.
         */
        this.resizeEnd = new EventEmitter(false);
        /**
         * @private
         */
        this.mouseup = new Subject();
        /**
         * @private
         */
        this.mousedown = new Subject();
        /**
         * @private
         */
        this.mousemove = new Subject();
    }
    /**
     * @private
     */
    Resizable.prototype.ngOnInit = function () {
        var _this = this;
        var currentResize;
        var removeGhostElement = function () {
            if (currentResize.clonedNode) {
                _this.elm.nativeElement.parentElement.removeChild(currentResize.clonedNode);
                _this.renderer.setElementStyle(_this.elm.nativeElement, 'visibility', 'inherit');
            }
        };
        this.mousemove.subscribe(function (_a) {
            var mouseX = _a.mouseX, mouseY = _a.mouseY, event = _a.event;
            if (currentResize) {
                event.preventDefault();
            }
            var resizeEdges = getResizeEdges({
                mouseX: mouseX, mouseY: mouseY,
                elm: _this.elm,
                allowedEdges: _this.resizeEdges,
                cursorPrecision: _this.resizeCursorPrecision
            });
            var resizeCursors = Object.assign({}, DEFAULT_RESIZE_CURSORS, _this.resizeCursors);
            var cursor = currentResize ? null : getResizeCursor(resizeEdges, resizeCursors);
            _this.renderer.setElementStyle(_this.elm.nativeElement, 'cursor', cursor);
            _this.renderer.setElementClass(_this.elm.nativeElement, RESIZE_ACTIVE_CLASS, !!currentResize);
            _this.renderer.setElementClass(_this.elm.nativeElement, RESIZE_LEFT_HOVER_CLASS, resizeEdges.left === true);
            _this.renderer.setElementClass(_this.elm.nativeElement, RESIZE_RIGHT_HOVER_CLASS, resizeEdges.right === true);
            _this.renderer.setElementClass(_this.elm.nativeElement, RESIZE_TOP_HOVER_CLASS, resizeEdges.top === true);
            _this.renderer.setElementClass(_this.elm.nativeElement, RESIZE_BOTTOM_HOVER_CLASS, resizeEdges.bottom === true);
        });
        var mousedrag = this.mousedown.flatMap(function (startCoords) {
            var getDiff = function (moveCoords) {
                return {
                    mouseX: moveCoords.mouseX - startCoords.mouseX,
                    mouseY: moveCoords.mouseY - startCoords.mouseY
                };
            };
            var getSnapGrid = function () {
                var snapGrid = { x: 1, y: 1 };
                if (currentResize) {
                    if (_this.resizeSnapGrid.left && currentResize.edges.left) {
                        snapGrid.x = +_this.resizeSnapGrid.left;
                    }
                    else if (_this.resizeSnapGrid.right && currentResize.edges.right) {
                        snapGrid.x = +_this.resizeSnapGrid.right;
                    }
                    if (_this.resizeSnapGrid.top && currentResize.edges.top) {
                        snapGrid.y = +_this.resizeSnapGrid.top;
                    }
                    else if (_this.resizeSnapGrid.bottom && currentResize.edges.bottom) {
                        snapGrid.y = +_this.resizeSnapGrid.bottom;
                    }
                }
                return snapGrid;
            };
            var getGrid = function (coords, snapGrid) {
                return {
                    x: Math.ceil(coords.mouseX / snapGrid.x),
                    y: Math.ceil(coords.mouseY / snapGrid.y)
                };
            };
            return merge(_this.mousemove.take(1).map(function (coords) { return [, coords]; }), _this.mousemove.pairwise()).map(function (_a) {
                var previousCoords = _a[0], newCoords = _a[1];
                return [previousCoords ? getDiff(previousCoords) : previousCoords, getDiff(newCoords)];
            }).filter(function (_a) {
                var previousCoords = _a[0], newCoords = _a[1];
                if (!previousCoords) {
                    return true;
                }
                var snapGrid = getSnapGrid();
                var previousGrid = getGrid(previousCoords, snapGrid);
                var newGrid = getGrid(newCoords, snapGrid);
                return (previousGrid.x !== newGrid.x || previousGrid.y !== newGrid.y);
            }).map(function (_a) {
                var newCoords = _a[1];
                var snapGrid = getSnapGrid();
                return {
                    mouseX: Math.round(newCoords.mouseX / snapGrid.x) * snapGrid.x,
                    mouseY: Math.round(newCoords.mouseY / snapGrid.y) * snapGrid.y
                };
            }).takeUntil(merge(_this.mouseup, _this.mousedown));
        }).filter(function () { return !!currentResize; });
        mousedrag.map(function (_a) {
            var mouseX = _a.mouseX, mouseY = _a.mouseY;
            return getNewBoundingRectangle(currentResize.startingRect, currentResize.edges, mouseX, mouseY);
        }).filter(function (newBoundingRect) {
            return newBoundingRect.height > 0 && newBoundingRect.width > 0;
        }).filter(function (newBoundingRect) {
            return _this.validateResize ? _this.validateResize({
                rectangle: newBoundingRect,
                edges: getEdgesDiff({
                    edges: currentResize.edges,
                    initialRectangle: currentResize.startingRect,
                    newRectangle: newBoundingRect
                })
            }) : true;
        }).subscribe(function (newBoundingRect) {
            if (currentResize.clonedNode) {
                _this.renderer.setElementStyle(currentResize.clonedNode, 'height', newBoundingRect.height + "px");
                _this.renderer.setElementStyle(currentResize.clonedNode, 'width', newBoundingRect.width + "px");
                _this.renderer.setElementStyle(currentResize.clonedNode, 'top', newBoundingRect.top + "px");
                _this.renderer.setElementStyle(currentResize.clonedNode, 'left', newBoundingRect.left + "px");
            }
            _this.zone.run(function () {
                _this.resizing.emit({
                    edges: getEdgesDiff({
                        edges: currentResize.edges,
                        initialRectangle: currentResize.startingRect,
                        newRectangle: newBoundingRect
                    }),
                    rectangle: newBoundingRect
                });
            });
            currentResize.currentRect = newBoundingRect;
        });
        this.mousedown.map(function (_a) {
            var mouseX = _a.mouseX, mouseY = _a.mouseY, edges = _a.edges;
            return edges || getResizeEdges({
                mouseX: mouseX, mouseY: mouseY,
                elm: _this.elm,
                allowedEdges: _this.resizeEdges,
                cursorPrecision: _this.resizeCursorPrecision
            });
        }).filter(function (edges) {
            return Object.keys(edges).length > 0;
        }).subscribe(function (edges) {
            if (currentResize) {
                removeGhostElement();
            }
            var startingRect = _this.elm.nativeElement.getBoundingClientRect();
            currentResize = {
                edges: edges,
                startingRect: startingRect,
                currentRect: startingRect
            };
            if (_this.enableGhostResize) {
                currentResize.clonedNode = _this.elm.nativeElement.cloneNode(true);
                var resizeCursors = Object.assign({}, DEFAULT_RESIZE_CURSORS, _this.resizeCursors);
                _this.elm.nativeElement.parentElement.appendChild(currentResize.clonedNode);
                _this.renderer.setElementStyle(_this.elm.nativeElement, 'visibility', 'hidden');
                _this.renderer.setElementStyle(currentResize.clonedNode, 'position', 'fixed');
                _this.renderer.setElementStyle(currentResize.clonedNode, 'left', currentResize.startingRect.left + "px");
                _this.renderer.setElementStyle(currentResize.clonedNode, 'top', currentResize.startingRect.top + "px");
                _this.renderer.setElementStyle(currentResize.clonedNode, 'height', currentResize.startingRect.height + "px");
                _this.renderer.setElementStyle(currentResize.clonedNode, 'width', currentResize.startingRect.width + "px");
                _this.renderer.setElementStyle(currentResize.clonedNode, 'cursor', getResizeCursor(currentResize.edges, resizeCursors));
            }
            _this.zone.run(function () {
                _this.resizeStart.emit({
                    edges: getEdgesDiff({ edges: edges, initialRectangle: startingRect, newRectangle: startingRect }),
                    rectangle: getNewBoundingRectangle(startingRect, {}, 0, 0)
                });
            });
        });
        this.mouseup.subscribe(function () {
            if (currentResize) {
                _this.renderer.setElementClass(_this.elm.nativeElement, RESIZE_ACTIVE_CLASS, false);
                _this.zone.run(function () {
                    _this.resizeEnd.emit({
                        edges: getEdgesDiff({
                            edges: currentResize.edges,
                            initialRectangle: currentResize.startingRect,
                            newRectangle: currentResize.currentRect
                        }),
                        rectangle: currentResize.currentRect
                    });
                });
                removeGhostElement();
                currentResize = null;
            }
        });
    };
    /**
     * @private
     */
    Resizable.prototype.ngAfterViewInit = function () {
        var _this = this;
        this.resizeHandles.forEach(function (handle) {
            handle.resizable = _this;
        });
    };
    /**
     * @private
     */
    Resizable.prototype.ngOnDestroy = function () {
        this.mousedown.complete();
        this.mouseup.complete();
        this.mousemove.complete();
    };
    /**
     * @private
     */
    Resizable.prototype.onMousedown = function (mouseX, mouseY) {
        var _this = this;
        this.zone.runOutsideAngular(function () {
            _this.mousedown.next({ mouseX: mouseX, mouseY: mouseY });
        });
    };
    /**
     * @private
     */
    Resizable.prototype.onMousemove = function (event, mouseX, mouseY) {
        var _this = this;
        this.zone.runOutsideAngular(function () {
            _this.mousemove.next({ mouseX: mouseX, mouseY: mouseY, event: event });
        });
    };
    /**
     * @private
     */
    Resizable.prototype.onMouseup = function (mouseX, mouseY) {
        var _this = this;
        this.zone.runOutsideAngular(function () {
            _this.mouseup.next({ mouseX: mouseX, mouseY: mouseY });
        });
    };
    Resizable.decorators = [
        { type: Directive, args: [{
                    selector: '[mwlResizable]'
                },] },
    ];
    /** @nocollapse */
    Resizable.ctorParameters = function () { return [
        { type: Renderer, },
        { type: ElementRef, },
        { type: NgZone, },
    ]; };
    Resizable.propDecorators = {
        'validateResize': [{ type: Input },],
        'resizeEdges': [{ type: Input },],
        'enableGhostResize': [{ type: Input },],
        'resizeSnapGrid': [{ type: Input },],
        'resizeCursors': [{ type: Input },],
        'resizeCursorPrecision': [{ type: Input },],
        'resizeStart': [{ type: Output },],
        'resizing': [{ type: Output },],
        'resizeEnd': [{ type: Output },],
        'resizeHandles': [{ type: ContentChildren, args: [ResizeHandle,] },],
        'onMousedown': [{ type: HostListener, args: ['document:touchstart', ['$event.touches[0].clientX', '$event.touches[0].clientY'],] }, { type: HostListener, args: ['document:mousedown', ['$event.clientX', '$event.clientY'],] },],
        'onMousemove': [{ type: HostListener, args: ['document:touchmove', ['$event', '$event.targetTouches[0].clientX', '$event.targetTouches[0].clientY'],] }, { type: HostListener, args: ['document:mousemove', ['$event', '$event.clientX', '$event.clientY'],] },],
        'onMouseup': [{ type: HostListener, args: ['document:touchend', ['$event.changedTouches[0].clientX', '$event.changedTouches[0].clientY'],] }, { type: HostListener, args: ['document:touchcancel', ['$event.changedTouches[0].clientX', '$event.changedTouches[0].clientY'],] }, { type: HostListener, args: ['document:mouseup', ['$event.clientX', '$event.clientY'],] },],
    };
    return Resizable;
}());
//# sourceMappingURL=resizable.directive.js.map


//////////////////
// WEBPACK FOOTER
// ./~/angular-resizable-element/dist/esm/src/resizable.directive.js
// module id = 1268
// module chunks = 4